import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import Business from "@mui/icons-material/Business";
import Groups from "@mui/icons-material/Groups";
import Lock from "@mui/icons-material/Lock";
import Public from "@mui/icons-material/Public";
import { SvgIcon } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { SkillAudience, SkillScope } from "generated-graphql/graphql";

/**
 * How one scope or audience value looks everywhere skills appear: the manage
 * grid, the editor's chips, the slash menu and the skill popover. Each surface
 * picks which parts to render.
 */
export type SkillAccessToken<T extends string> = {
  value: T;
  Icon: typeof SvgIcon;
  /** Short name, for chips, filter pills and captions. */
  label: string;
  /** Plain-language meaning, beside the chip in the picker menu. */
  description: string;
  /** Neutral, except Staff, which carries the staff accent. See {@link skillAccessTint}. */
  color: "default" | "staff";
};

export const SKILL_SCOPE_TOKENS: Record<SkillScope, SkillAccessToken<SkillScope>> = {
  [SkillScope.Tenant]: {
    value: SkillScope.Tenant,
    Icon: Business,
    label: "Your organization",
    description: "This organization only",
    color: "default",
  },
  [SkillScope.Global]: {
    value: SkillScope.Global,
    Icon: Public,
    label: "Encamp",
    description: "Every organization",
    color: "default",
  },
};

export const SKILL_AUDIENCE_TOKENS: Record<SkillAudience, SkillAccessToken<SkillAudience>> = {
  [SkillAudience.Private]: {
    value: SkillAudience.Private,
    Icon: Lock,
    label: "Only you",
    description: "Hidden from everyone else",
    color: "default",
  },
  [SkillAudience.Staff]: {
    value: SkillAudience.Staff,
    Icon: BadgeOutlined,
    label: "Staff",
    description: "Hidden from customers",
    color: "staff",
  },
  [SkillAudience.Customer]: {
    value: SkillAudience.Customer,
    Icon: Groups,
    label: "Everyone",
    description: "Customers and staff",
    color: "default",
  },
};

/** Picker and filter order. */
export const SKILL_SCOPE_OPTIONS = [SkillScope.Tenant, SkillScope.Global].map(
  (scope) => SKILL_SCOPE_TOKENS[scope]
);
export const SKILL_AUDIENCE_OPTIONS = [
  SkillAudience.Private,
  SkillAudience.Staff,
  SkillAudience.Customer,
].map((audience) => SKILL_AUDIENCE_TOKENS[audience]);

/** One-icon summary of scope and audience, as the slash menu groups skills. */
export type SkillOwner = "encamp" | "org" | "mine";

export const SKILL_OWNER_TOKENS: Record<SkillOwner, SkillAccessToken<string>> = {
  encamp: SKILL_SCOPE_TOKENS[SkillScope.Global],
  org: SKILL_SCOPE_TOKENS[SkillScope.Tenant],
  mine: SKILL_AUDIENCE_TOKENS[SkillAudience.Private],
};

/** PRIVATE wins over scope: a private skill is "mine" wherever it activates. */
export function skillOwner(skill: {
  scope: SkillScope;
  audience?: SkillAudience | null;
}): SkillOwner {
  if (skill.audience === SkillAudience.Private) {
    return "mine";
  }
  return skill.scope === SkillScope.Global ? "encamp" : "org";
}

/** "Your organization · Everyone": where a skill activates and who can see it. */
export function skillAccessCaption(skill: { scope: SkillScope; audience: SkillAudience }): string {
  return `${SKILL_SCOPE_TOKENS[skill.scope].label} · ${SKILL_AUDIENCE_TOKENS[skill.audience].label}`;
}

/**
 * Soft fill and text colour for a token's chip or pill: a faint wash rather
 * than a border, so the staff accent reads without shouting.
 */
export function skillAccessTint(
  theme: Theme,
  color: SkillAccessToken<string>["color"],
  { hover = false }: { hover?: boolean } = {}
) {
  if (color === "staff") {
    return {
      bgcolor: alpha(theme.palette.staff.main, hover ? 0.14 : 0.08),
      color: theme.palette.staff.dark,
    };
  }
  return {
    bgcolor: alpha(theme.palette.text.primary, hover ? 0.1 : 0.06),
    color: theme.palette.text.primary,
  };
}
